import React, { useState } from "react"

import { projectPickerLabel } from "../../../components/Shared/ProjectPickerSelect.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { invoiceAddPermissions } from "../helpers/invoice-add-permissions.ts"
import {
  AddInvoiceMenu,
  type AddInvoiceKind
} from "./AddInvoiceMenu.tsx"
import { selectProjectWritePermissions } from "../../Project/helpers/project-permissions.selectors.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import { ExternalInvoiceDrawer } from "./ExternalInvoiceDrawer.tsx"
import { IssueInvoiceDrawer } from "./IssueInvoiceDrawer.tsx"

interface QuickAddInvoiceControlProps {
  projectId: string
  userId?: string
  selfAssigned?: boolean
  paymentAmount?: number | null
  preselectedLineId?: string
  onCreated: (invoice: ProjectInvoice) => void
  tooltip?: string
  children: React.ReactElement
}

/**
 * "Quick-add invoice" from a payment or a finance row: the same choice every
 * other add offers, and whichever invoice comes back is the one selected.
 */
export function QuickAddInvoiceControl({
  projectId,
  userId,
  selfAssigned,
  paymentAmount,
  preselectedLineId,
  onCreated,
  tooltip,
  children
}: QuickAddInvoiceControlProps) {
  const [openKind, setOpenKind] = useState<AddInvoiceKind | null>(null)
  const project = useAppSelector((state) => state.project?.project)
  const invoiceCodes = useAppSelector(
    (state) => selectProjectWritePermissions(state)?.invoices
  )
  const userRoleId = useAppSelector((state) => state.auth?.userInfo?.role?.id)
  const { generateAllowed, externalAllowed } = invoiceAddPermissions(
    invoiceCodes,
    userRoleId
  )

  const handleSaved = (invoice?: ProjectInvoice) => {
    setOpenKind(null)
    if (invoice) onCreated(invoice)
  }

  return (
    <>
      <AddInvoiceMenu
        generateAllowed={generateAllowed}
        externalAllowed={externalAllowed}
        tooltip={tooltip}
        onSelect={setOpenKind}
      >
        {children}
      </AddInvoiceMenu>

      <ExternalInvoiceDrawer
        open={openKind === "external"}
        projectId={projectId}
        onClose={() => setOpenKind(null)}
        onSaved={handleSaved}
        selfAssigned={selfAssigned}
        userId={userId}
        paymentAmount={paymentAmount}
        preselectedLineId={preselectedLineId}
      />

      {generateAllowed && (
        <IssueInvoiceDrawer
          open={openKind === "generated"}
          projectId={projectId}
          projectLabel={projectPickerLabel(project)}
          preselectedLineId={preselectedLineId}
          hideDraft
          onClose={() => setOpenKind(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  )
}
